/*!  =========================================================
 * Argon Design System React - v1.1.2
 =========================================================
 * Product Page: https://www.creative-tim.com/product/argon-design-system-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)
 * Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)
 * Coded by Creative Tim
 =========================================================
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import React from 'react';

// reactstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Progress,
  Badge,
  Modal,
  CardBody,
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  UncontrolledTooltip,
  Spinner
} from "reactstrap";

// core components
import SimpleFooter from "../components/general/SimpleFooter";
import classnames from "classnames";

import '../assets/css/picker.css';
import { RiArrowDownLine, RiArrowUpLine, RiDeleteBinFill } from '@remixicon/react';
import {
  RiAddLine,
  RiArchive2Fill,
  RiBookMarkedFill,
  RiCloseLine,
  RiDraftLine,
  RiFolderMusicLine,
  RiQuestionLine,
  RiShare2Line,
  RiUserVoiceLine,
  RiVoiceAiFill
} from "react-icons/ri";

import { emailPlaylist, savePlaylist } from "../components/helpers/CuratedPlaylistHelpers";
import { adminUpdateSongFlowPublishing } from "../components/helpers/PublicCuratedSongFlowHelpers";
import CuratedSongFlowSignupBanner from "../components/helpers/CuratedSongFlowSignupBanner";
import withNavigateHook from "../hooks/withNavigationHook";
import AutoCompleteInput from "./song/AutoCompleteInput";
import SeoNoIndex from "../components/helpers/SeoNoIndex";
import {SeoHelper} from "../components/helpers/SeoHelper";
import KstTrackLinks from "../components/kst/KstTrackLinks";
import ServicePartCard from "../components/service-plan/ServicePartCard";

function AdminToggle({ checked, disabled, onChange, label }) {
  return (
      <button
          type="button"
          onClick={() => !disabled && onChange(!checked)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            background: 'none', border: 'none', padding: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            fontSize: '0.875rem', color: '#32325d',
          }}
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '16px', height: '16px', flexShrink: 0,
          border: `2px solid ${checked ? '#5e72e4' : '#adb5bd'}`,
          borderRadius: '3px', background: checked ? '#5e72e4' : '#fff',
          color: '#fff', fontSize: '11px', lineHeight: 1,
        }}>
          {checked ? '✓' : ''}
        </span>
        {label}
      </button>
  );
}

class CuratedSongsPicker extends React.Component {
  constructor() {
    super();
    // Extended state to include new input fields
    this.state = {
      value: '',
      items: [],
      draggingItem: null,
      newItemName: '',
      newItemImage: '',
      saveNotes: '',
      yourName: '',         // NEW: Sender's name
      recipients: [{ name: '', email: '', emailError: '' }],
      saveName: '',
      saveNameError: '',
      formModalShare: false,
      formModalSent: false,
      formModalSave: false,
      isLocked: false,
      removedStack: [],
      isSharing: false,
      shareError: null,
      shareableCuratedFlow: false,
      published: false,
      publicSlug: null,
      publishingUpdating: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  // New change handlers for the additional input fields
  onChangeYourName = (e) => {
    this.setState({ yourName: e.target.value });
  };

  resetRecipients = () => {
    this.setState({ recipients: [{ name: '', email: '', emailError: '' }], shareError: null });
  };

  openShareModal = () => {
    this.resetRecipients();
    this.toggleModal("formModalShare");
  };

  onChangeRecipientName = (index, value) => {
    const recipients = [...this.state.recipients];
    recipients[index] = { ...recipients[index], name: value };
    this.setState({ recipients });
  };

  onChangeRecipientEmail = (index, value) => {
    const recipients = [...this.state.recipients];
    recipients[index] = { ...recipients[index], email: value, emailError: '' };
    this.setState({ recipients });
  };

  addRecipient = () => {
    this.setState({
      recipients: [...this.state.recipients, { name: '', email: '', emailError: '' }]
    });
  };

  removeRecipient = (index) => {
    if (this.state.recipients.length <= 1) {
      return;
    }

    this.setState({
      recipients: this.state.recipients.filter((recipient, recipientIndex) => recipientIndex !== index)
    });
  };

  handleDragStart = (e, item) => {
    this.setState({ draggingItem: item });
    e.dataTransfer.setData('text/plain', '');
  };

  handleDragEnd = () => {
    this.setState({ draggingItem: null });
  };

  handleDragOver = (e) => {
    e.preventDefault();
  };

  handleDrop = (e, targetItem) => {
    const { draggingItem, items } = this.state;
    if (!draggingItem) return;

    const currentIndex = items.indexOf(draggingItem);
    const targetIndex = items.indexOf(targetItem);

    if (currentIndex !== -1 && targetIndex !== -1) {
      items.splice(currentIndex, 1);
      items.splice(targetIndex, 0, draggingItem);
      this.setState({ items });
    }

    // Reassign `i` for ordering on curated song list
    let recalibratedItems = [];
    for (let i = 0; i < items.length; i++) {
      const current = items[i];
      current.i = i;
      recalibratedItems.push(current);
    }
    sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));
  };

  moveItem = (key, direction) => {
    const items = [...this.state.items];
    const index = items.findIndex(item => item.key === key);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    const recalibrated = items.map((item, i) => ({ ...item, i, playOrder: i }));
    sessionStorage.setItem("curated-songs", JSON.stringify(recalibrated));
    this.setState({ items: recalibrated });
  };

  updateServicePartItem = (key, fields) => {
    const items = this.state.items.map(item =>
        item.key === key ? { ...item, ...fields } : item
    );
    sessionStorage.setItem("curated-songs", JSON.stringify(items));
    this.setState({ items });
  };

  handleNameChange = (e) => {
    this.setState({ newItemName: e.target.value });
  };

  handleImageChange = (e) => {
    this.setState({ newItemImage: e.target.value });
  };

  addNewItem = () => {
    // Generate a unique ID for the new item
    const newItemId = Math.max(...this.state.items.map((item) => item.id)) + 1;
    const newItem = {
      id: newItemId,
      name: this.state.newItemName,
      image: this.state.newItemImage,
    };

    // Add the new item to the state
    this.setState({
      items: [...this.state.items, newItem],
      newItemName: '', // Clear the input fields
      newItemImage: '',
    });
  };

  toggleModal = (state) => {
    this.setState({
      [state]: !this.state[state],
    });
  };

  componentDidMount() {
    this.setState({
      items: !this.state.items || this.state.items.length === 0
          ? sessionStorage.getItem("curated-songs") == null ? [] : JSON.parse(sessionStorage.getItem("curated-songs"))
          : this.state.items
    });

    this.state.items.sort((a, b) => a.i > b.i);

    const passages = sessionStorage.getItem("selected-passages") == null ? [] : JSON.parse(sessionStorage.getItem("selected-passages"));
    const themes = sessionStorage.getItem("selected-themes") == null ? [] : JSON.parse(sessionStorage.getItem("selected-themes"));

    this.setState({
      choosenPassageButtons: passages
          .sort((a, b) => a.displayOrder > b.displayOrder ? 1 : -1)
          .map((passage, index) => {
            return (
                <div className="col-4" key={index}>
                  <Badge className='text-uppercase mx-2' color='primary' pill>
                    {passage.book} {passage.chapter}
                    {passage.verseFrom ? "v" + passage.verseFrom : null}
                    {passage.verseTo ? "-" + passage.verseTo : null}
                  </Badge>
                </div>
            );
          })
    });

    this.setState({
      chosenThemeButtons: themes.map((theme, index) => {
        return (
            <div className="col-4" key={index}>
              <Badge className='text-uppercase mx-2' color='primary' pill>
                {theme.title}
              </Badge>
            </div>
        );
      })
    });

    const swoCount = parseInt(localStorage.getItem("swo") || "0", 10);
    const isNotLoggedIn = !sessionStorage.getItem("customer");
    if (swoCount >= 3 && isNotLoggedIn) {
      this.setState({ isLocked: true, formModalUpgrade: true });
    }

    const savedPlaylist = sessionStorage.getItem("playlist") ? JSON.parse(sessionStorage.getItem("playlist")) : null;
    if (savedPlaylist) {
      this.setState({
        shareableCuratedFlow: savedPlaylist.shareableCuratedFlow || false,
        published: savedPlaylist.published || false,
        publicSlug: savedPlaylist.publicSlug || null,
      });
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'build_service', {
        source: 'curated'
      });
    }
  }

  isAdmin = () => {
    const customer = JSON.parse(sessionStorage.getItem("customer") || "null");
    return customer?.user?.isAdmin === true;
  };

  getSavedPlaylistId = () => {
    const p = sessionStorage.getItem("playlist");
    return p ? JSON.parse(p).id : null;
  };

  updatePublishing = (shareableCuratedFlow, published) => {
    const playlistId = this.getSavedPlaylistId();
    if (!playlistId) return;
    this.setState({ publishingUpdating: true });
    adminUpdateSongFlowPublishing(playlistId, shareableCuratedFlow, published)
        .then(result => {
          if (result) {
            this.setState({
              shareableCuratedFlow: result.shareableCuratedFlow || false,
              published: result.published || false,
              publicSlug: result.publicSlug || null,
            });
            const savedPlaylist = sessionStorage.getItem("playlist") ? JSON.parse(sessionStorage.getItem("playlist")) : {};
            sessionStorage.setItem("playlist", JSON.stringify({
              ...savedPlaylist,
              shareableCuratedFlow: result.shareableCuratedFlow,
              published: result.published,
              publicSlug: result.publicSlug,
            }));
          }
          this.setState({ publishingUpdating: false });
        });
  };

  handleChange(event) {
    this.setState({ value: event.target.value });
  }

  handleSubmit(event) {
    event.preventDefault();
    alert('A name was submitted: ' + this.state.value);
    event.preventDefault();
  }

  getQueryVariable = (variable) => {
    var query = window.location.search.substring(1);
    var vars = query.split("&");
    for (var i = 0; i < vars.length; i++) {
      var pair = vars[i].split("=");
      if (pair[0] === variable) { return pair[1]; }
    }
    return (false);
  }

  backToWhere = () => {
    return window.location.pathname.includes("themes") ? "/themes/songs" : "/scripture/songs";
  }

  saveToWhere = () => {
    return window.location.pathname.includes("themes") ? "/themes/curated-songs/save" : "/scripture/curated-songs/save";
  }

  backToHere = () => {
    return window.location.pathname.includes("themes") ? "/themes/curated-songs" : "/scripture/curated-songs";
  }

  onChangeSaveName = (e) => {
    this.setState({
      saveName: e.target.value,
      saveNameError: "",
    });
  };

  onChangeSaveNotes = (e) => {
    this.setState({
      saveNotes: e.target.value,
      saveNotesError: "",
    });
  };

  savePlaylist = () => {
    this.setState({ saveFormSubmitted: true });
    let formHasError = false;

    if (!this.state.saveName || this.state.saveName.trim().length === 0) {
      this.setState({ firstNameError: "* You must provide a name" })
      formHasError = true;
    }

    if (!formHasError) {
      this.setState({ isLoading: true });
      savePlaylist(this.state.saveName, this.state.saveNotes)
        .then(result => {
          this.props.navigation("/playlist/" + result.id + "/" + result.name);
          this.setState({ isLoading: false });
        })
        .catch(err => {
          console.error("Error saving playlist:", err);
          this.setState({ isLoading: false });
        });
      this.setState({ saveFormSubmitted: true });
    } else {
      this.setState({ saveFormSubmitted: true });
    }
  }

  getTitle = (objectWithTitle) => {
    return objectWithTitle.map((object, index) => {
      return <small className="font-weight-light" key={index}><p>{object.title}</p></small>;
    });
  }

  removeFromCuratedList = (key) => {
    this.setState((prev) => {
      const index = prev.items.findIndex(item => item.key === key);
      if (index === -1) return null;

      const removedItem = prev.items[index];

      const newItems = [...prev.items];
      newItems.splice(index, 1);

      // Reassign ordering
      const recalibratedItems = newItems.map((item, i) => ({
        ...item,
        i
      }));

      sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));

      return {
        items: recalibratedItems,
        removedStack: [...prev.removedStack, { item: removedItem, index }]
      };
    });
  };

  undoLastRemoval = () => {
    this.setState((prev) => {
      if (prev.removedStack.length === 0) return null;

      const stack = [...prev.removedStack];
      const last = stack.pop(); // LIFO

      const newItems = [...prev.items];
      newItems.splice(last.index, 0, last.item);

      const recalibratedItems = newItems.map((item, i) => ({
        ...item,
        i
      }));

      sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));

      return {
        items: recalibratedItems,
        removedStack: stack
      };
    });
  };

  sharePlaylist = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const recipients = this.state.recipients.map(recipient => ({
      ...recipient,
      emailError: emailRegex.test(recipient.email) ? '' : 'Please enter a valid email address'
    }));
    const hasErrors = recipients.some(recipient => recipient.emailError);

    if (hasErrors) {
      this.setState({ recipients });
      return;
    }

    this.setState({ isSharing: true, shareError: null });
    const senderName = sessionStorage.getItem("customer") ? JSON.parse(sessionStorage.getItem("customer")).user.firstName : this.state.yourName;
    emailPlaylist(
        this.state.saveNotes,
        JSON.parse(sessionStorage.getItem("curated-songs")),
        senderName,
        this.state.recipients.map(r => ({ name: r.name, email: r.email }))
    ).then((result) => {
      if (result) {
        this.setState({ isSharing: "done" });
        setTimeout(() => {
          this.setState({ isSharing: false, recipients: [{ name: '', email: '', emailError: '' }], shareError: null });
          this.toggleModal("formModalShare");
          this.toggleModal("formModalSent");

          const sharedWithOthers = !localStorage.getItem("swo") ? 0 : parseInt(localStorage.getItem("swo"));
          localStorage.setItem("swo", sharedWithOthers + 1);
        }, 800);
      } else {
        this.setState({ isSharing: false, shareError: "Something went wrong — please try again." });
      }
    });
  };

  renderScriptureChoosePassages = () => {
    return "/scripture/choose-passages";
  }

  renderThemesChoosePassages = () => {
    return "/themes/choose-themes";
  }

  handleBack = () => {
    if (window.history.length > 1) {
      this.props.navigation(-1);
    } else {
      this.props.navigation("/");
    }
  };

  render() {
    return (
        <>
          <SeoHelper
              title = "Curate your Song-Flow"
              noindex={true}
          />
          <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows{" "}
                        <span>curate your song-flow</span>
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                </svg>
              </div>
            </section>
          </div>
          <section className="section section-lg pt-lg-0 mt--200">
            <div className={this.state.isLocked ? "locked-overlay" : ""}>
              <Container>
                <Row className="justify-content-center">
                  <Col lg="12">
                    <Row className="row-grid">
                      <Col lg="12">
                        <Card className="card-hover shadow border-0">
                          <CardBody className="py-5">
                            <Row>
                              <Col>
                                <h2 className="text-primary text-uppercase" style={{fontSize: '1rem'}}>
                                  <RiFolderMusicLine color='#5e72e4' size="36"/>&nbsp;Your song-flow<b></b>
                                  <Button className="btn-tooltip mt--1 ml-3" color="neutral" id="tooltipDraft" size="sm">
                                    draft&nbsp;<RiQuestionLine/>
                                  </Button>
                                  <UncontrolledTooltip
                                      delay={0}
                                      placement="right"
                                      target="tooltipDraft"
                                      trigger="hover focus"
                                  >
                                    Login to be able to save curated song-flows
                                  </UncontrolledTooltip>
                                </h2>
                              </Col>
                              <Col className="text-right">

                                {this.state.removedStack.length > 0 && (
                                    <Button
                                        className="btn-icon mb-3 mr-2"
                                        color="warning"
                                        type="button"
                                        onClick={this.undoLastRemoval}
                                    >
                                      <span className="btn-inner--icon">
                                        <i className="ni ni-undo" />
                                      </span>
                                      <span className="btn-inner--text">
                                        Undo ({this.state.removedStack.length})
                                      </span>
                                    </Button>
                                )}

                                <Button
                                    className="btn-icon mb-3"
                                    color="secondary"
                                    type="button"
                                    onClick={this.handleBack}
                                >
                                  <span className="btn-inner--icon">
                                    <i className="ni ni-bold-left" />
                                  </span>
                                  <span className="btn-inner--text">Back</span>
                                </Button>

                              </Col>
                            </Row>
                            <p className="description mt-3">
                              <FormGroup className={classnames("mb-3", {focused: this.state.saveNotesFocused})}>
                                <InputGroup className="input-group-alternative">
                                  <InputGroupText>
                                    <RiDraftLine/>
                                  </InputGroupText>
                                  <Input
                                      placeholder="Type your notes here"
                                      type="textarea"
                                      onFocus={(e) => this.setState({saveNotesFocused: true})}
                                      onBlur={(e) => this.setState({saveNotesFocused: false})}
                                      onChange={this.onChangeSaveNotes}
                                  />
                                </InputGroup>
                              </FormGroup>
                            </p>
                            <div className="sortable-list" style={{width: '100%'}}>
                              {this.state.items && this.state.items.length > 0 &&
                                  this.state.items.map((item, index) => {
                                    const isFirst = index === 0;
                                    const isLast = index === this.state.items.length - 1;
                                    const dragHandlers = {
                                      onDragStart: (e) => this.handleDragStart(e, item),
                                      onDragEnd: this.handleDragEnd,
                                      onDragOver: this.handleDragOver,
                                      onDrop: (e) => this.handleDrop(e, item)
                                    };
                                    if (item.itemType === 'SERVICE_PART') {
                                      return (
                                          <ServicePartCard
                                              key={item.key}
                                              item={item}
                                              onRemove={this.removeFromCuratedList}
                                              onUpdate={this.updateServicePartItem}
                                              onMoveUp={isFirst ? null : () => this.moveItem(item.key, -1)}
                                              onMoveDown={isLast ? null : () => this.moveItem(item.key, 1)}
                                              isDragging={item === this.state.draggingItem}
                                              dragHandlers={dragHandlers}
                                          />
                                      );
                                    }
                                    return (
                                      <div
                                          key={item.key}
                                          className={`item ${item === this.state.draggingItem ? 'dragging' : ''}`}
                                          draggable="true"
                                          onDragStart={(e) => this.handleDragStart(e, item)}
                                          onDragEnd={this.handleDragEnd}
                                          onDragOver={this.handleDragOver}
                                          onDrop={(e) => this.handleDrop(e, item)}
                                      >
                                        <div className="align-items-start col-md-12 col-xs-12 row">
                                          <div className="col-md-6 col-xs-12" style={{fontWeight: "bold"}}>
                                            {/* Song title */}
                                            {item.songTitle}
                                            <KstTrackLinks kstTracks={item.kstTracks} />
                                            <br/>
                                            {/* Alternative title */}
                                            {!item.alternativeTitle ? null : <>({item.alternativeTitle})</>}

                                            {/* ← Add authors here ↓ */}
                                            {item.authorNames && (
                                                <div style={{fontWeight: 'normal', fontSize: '0.85rem', color: '#555'}}>
                                                  {item.authorNames}
                                                </div>
                                            )}
                                          </div>

                                          <div className="col-md-4 col-xs-12">
                                            {item.songBookTitle}&nbsp;&nbsp;({item.songBookSongNumber})
                                            {(() => {
                                              const parts = [];
                                              if (item.keySignature)  parts.push(`Key: ${item.keySignature}`);
                                              if (item.timeSignature) parts.push(`Time Signature: ${item.timeSignature}`);
                                              if (item.tempoBpm)      parts.push(`Tempo: ${item.tempoBpm} bpm`);
                                              if (item.meter)         parts.push(`Metre: ${item.meter}`);
                                              if (item.ccliNumber)    parts.push(`CCLI: ${item.ccliNumber}`);
                                              return parts.length > 0
                                                  ? <div style={{ fontSize: "0.75rem", color: "#888" }}>{parts.join(' · ')}</div>
                                                  : null;
                                            })()}
                                          </div>
                                          <div className="col-md-2 col-xs-12">
                                            <Button
                                                color="link"
                                                className="p-0"
                                                onClick={() => this.removeFromCuratedList(item.key)}
                                            >
                                              <RiDeleteBinFill color="grey"/>
                                            </Button>
                                            &nbsp;
                                            <Button color="link" className="p-0" disabled={isFirst} onClick={() => this.moveItem(item.key, -1)} title="Move up">
                                              <RiArrowUpLine color={isFirst ? "#ccc" : "grey"} />
                                            </Button>
                                            <Button color="link" className="p-0" disabled={isLast} onClick={() => this.moveItem(item.key, 1)} title="Move down">
                                              <RiArrowDownLine color={isLast ? "#ccc" : "grey"} />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                              }
                              {(!this.state.items || this.state.items.length === 0) &&
                                  <>You currently have no songs in your song-flow</>
                              }
                            </div>

                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      &nbsp;
                    </Row>
                    {this.isAdmin() && this.getSavedPlaylistId() && (
                        <Row className="mb-3">
                          <Col lg="12">
                            <Card className="shadow border-0" style={{ borderLeft: '4px solid #5e72e4' }}>
                              <CardBody className="py-3 px-4">
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '20px' }}>
                                  <small className="text-uppercase text-muted font-weight-bold" style={{ minWidth: '50px' }}>Admin</small>
                                  <AdminToggle
                                      checked={this.state.shareableCuratedFlow}
                                      disabled={this.state.publishingUpdating}
                                      onChange={(v) => this.updatePublishing(v, this.state.published && v)}
                                      label="Allow public subscription"
                                  />
                                  {this.state.shareableCuratedFlow && (
                                      <AdminToggle
                                          checked={this.state.published}
                                          disabled={this.state.publishingUpdating}
                                          onChange={(v) => this.updatePublishing(true, v)}
                                          label="Published (live)"
                                      />
                                  )}
                                  {this.state.publicSlug && this.state.published && (
                                      <small>
                                        <a href={`/song-flows/${this.state.publicSlug}/subscribe`} target="_blank" rel="noopener noreferrer" style={{ color: '#5e72e4' }}>
                                          /song-flows/{this.state.publicSlug}/subscribe
                                        </a>
                                      </small>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                    )}
                    <Row className="justify-content-center">
                      <Col lg="12">
                        <Card className="card-hover shadow border-0">
                          <CardBody className="py-5">
                            <h2 className="text-primary text-uppercase d-flex align-items-center"
                                style={{fontSize: '1rem'}}>
                            <span className="icon icon-shape icon-shape-primary rounded-circle">
                              <RiUserVoiceLine/>
                            </span>
                              <span className="ml-3">Add a song by name</span>
                            </h2>
                            <p className="description mt-3">
                              <AutoCompleteInput navTo="/songs/search"/>
                            </p>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      &nbsp;
                    </Row>
                    <Row>
                      <Col md="6">
                        <Button
                            block
                            className="mb-3"
                            color="primary"
                            type="button"
                            href={this.renderScriptureChoosePassages()}
                        >
                        <span className="btn-inner--icon mr-3">
                          <RiBookMarkedFill/>
                        </span>
                          Add more from Scripture
                        </Button>
                      </Col>
                      <Col md="6">
                        <Button
                            block
                            className="mb-3"
                            color="primary"
                            type="button"
                            href={this.renderThemesChoosePassages()}
                        >
                        <span className="btn-inner--icon mr-3">
                          <RiArchive2Fill />
                        </span>
                          Add more from Theme
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="3">
                        <div className="btn-wrapper">

                        </div>
                      </Col>
                      <Col lg="5" />
                    </Row>
                    <div style={{
                      position: "fixed",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      backgroundColor: "white",
                      padding: "58px",
                      boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.2)",
                      zIndex: "1000"
                    }}>
                      <div className="col-md-12 col-xs-12 text-center"
                           style={{ position: "fixed", bottom: "70px", right: "0px", zIndex: "1000" }}>
                        <p className="description text-primary mb-3" style={{ fontWeight: 600 }}>
                          Save your song-flow to start building your service plan
                        </p>
                      </div>
                      <div className="col-md-12 col-xs-12"
                           style={{ position: "fixed", bottom: "20px", right: "0px", zIndex: "1000" }}>
                        <Button
                            block
                            className="mb-3"
                            color="primary"
                            type="button"
                            disabled={(!this.state.items || this.state.items.length === 0)}
                            onClick={() => this.toggleModal(sessionStorage.getItem("customer") ? "formModalSave" : "formModalUpgrade")}
                        >
                          <RiFolderMusicLine size="24" />&nbsp;
                          Save changes
                        </Button>
                      </div>
                    </div>
                    <Row>
                      <Col lg="6">
                        {/* Save Playlist Modal */}
                        <Modal
                            className="modal-dialog-centered"
                            size="sm"
                            isOpen={this.state.formModalSave}
                            toggle={() => this.toggleModal("formModalSave")}
                        >
                          <div className="modal-header">
                            <h2 style={{ fontSize: '1rem' }} className="modal-title" id="modal-title-default">
                              Save your curated song-flow ...
                            </h2>
                            <button
                                aria-label="Close"
                                className="close"
                                data-dismiss="modal"
                                type="button"
                                onClick={() => this.toggleModal("formModalSave")}
                            >
                              <span aria-hidden={true}>×</span>
                            </button>
                          </div>
                          <div className="modal-body p-0">
                            <Card className="bg-secondary shadow border-0">
                              <CardBody className="px-lg-5 py-lg-5">
                                <div className="text-center text-muted mb-4">
                                  As a member you can save and manage unlimited curated song-flows.
                                </div>
                                <Form role="form">
                                  <FormGroup className={classnames("mb-3", { focused: this.state.saveNameFocused })}>
                                    <InputGroup className="input-group-alternative">
                                      <InputGroupText>
                                        <RiDraftLine />
                                      </InputGroupText>
                                      <Input
                                          placeholder="Name"
                                          type="text"
                                          onFocus={(e) => this.setState({ saveNameFocused: true })}
                                          onBlur={(e) => this.setState({ saveNameFocused: false })}
                                          onChange={this.onChangeSaveName}
                                      />
                                    </InputGroup>
                                  </FormGroup>
                                  <FormGroup className={classnames("mb-3", { focused: this.state.saveNotesFocused })}>
                                    <InputGroup className="input-group-alternative">
                                      <InputGroupText>
                                        <RiDraftLine />
                                      </InputGroupText>
                                      <Input
                                          placeholder="Type your notes here"
                                          type="textarea"
                                          value={this.state.saveNotes}
                                          onFocus={(e) => this.setState({ saveNotesFocused: true })}
                                          onBlur={(e) => this.setState({ saveNotesFocused: false })}
                                          onChange={this.onChangeSaveNotes}
                                      />
                                    </InputGroup>
                                  </FormGroup>
                                  {this.state.isLoading ? (
                                      <div className="spinner-container">
                                        <Spinner animation="border" role="status">
                                          <span className="visually-hidden"></span>
                                        </Spinner>
                                        &nbsp;Saving...
                                      </div>
                                  ) : (
                                      <div className="text-center">
                                        <Button className="my-4" color="primary" type="button" onClick={this.savePlaylist}>
                                          <RiFolderMusicLine size="24" />&nbsp;
                                          <span className="btn-inner--text">Save</span>
                                        </Button>
                                      </div>
                                  )}
                                </Form>
                              </CardBody>
                            </Card>
                          </div>
                        </Modal>
                        <Modal
                            className="modal-dialog-centered"
                            size="sm"
                            isOpen={this.state.formModalUpgrade}
                            toggle={() => this.toggleModal("formModalUpgrade")}
                        >
                          <div className="modal-header">
                            <h2 style={{ fontSize: '1rem' }} className="modal-title" id="modal-title-default">
                              Save your curated song-flow ...
                            </h2>
                            <button
                                aria-label="Close"
                                className="close"
                                data-dismiss="modal"
                                type="button"
                                onClick={() => this.toggleModal("formModalUpgrade")}
                            >
                              <span aria-hidden={true}>×</span>
                            </button>
                          </div>
                          <div className="modal-body p-0">
                            <Card className="bg-secondary shadow border-0">
                              <CardBody className="px-lg-5 py-lg-5">
                                <div className="text-center text-muted mb-4">
                                  Create an account to save and revisit your song-flows. Members can save, share, and build unlimited song-flows.
                                </div>
                                <div className="text-center">
                                  <Button
                                      className="my-4"
                                      color="primary"
                                      type="button"
                                      onClick={() => this.props.navigation("/register/choose-your-plan")}
                                  >
                                    <RiFolderMusicLine size="18" />
                                    <span className="btn-inner--text">View benefits</span>
                                  </Button>
                                </div>
                                <div className="text-center">
                                  <Button
                                      color="link"
                                      type="button"
                                      onClick={() => this.toggleModal("formModalUpgrade")}
                                  >
                                    Continue without saving
                                  </Button>
                                </div>
                              </CardBody>
                            </Card>
                          </div>
                        </Modal>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <Button
                            block
                            type="submit"
                            className="mb-3"
                            color="primary"
                            href="/lectionary/choose-lectionary"
                        >
                          <RiShare2Line />
                          <span className="btn-inner--text">Add from Lectionary</span>
                        </Button>
                      </Col>
                      <Col md="6">
                        <Button
                            block
                            type="submit"
                            className="mb-3"
                            color="neutral"
                            onClick={this.openShareModal}
                        >
                          <RiShare2Line />
                          <span className="btn-inner--text">Share with others</span>
                        </Button>
                        {/* Modified Share Modal with new input fields */}
                        <Modal
                            className="modal-dialog-centered"
                            size="md"
                            isOpen={this.state.formModalShare}
                            toggle={() => this.toggleModal("formModalShare")}
                        >
                          <div className="modal-header">
                            <div>
                              <h2 style={{ fontSize: '1rem' }} className="modal-title" id="modal-title-default">
                                Share your curated song-flow ...
                              </h2>
                              {!sessionStorage.getItem("customer") && (() => {
                                const used = parseInt(localStorage.getItem("swo") || "0", 10);
                                const remaining = Math.max(0, 3 - used);
                                return (
                                  <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.8rem" }}>
                                    You have {remaining} of 3 shares remaining
                                  </p>
                                );
                              })()}
                            </div>
                            <button
                                aria-label="Close"
                                className="close"
                                data-dismiss="modal"
                                type="button"
                                onClick={() => this.toggleModal("formModalShare")}
                            >
                              <span aria-hidden={true}>×</span>
                            </button>
                          </div>
                          <div className="modal-body p-0">
                            <Card className="bg-secondary shadow border-0">
                              <CardBody className="px-lg-5 py-lg-5">
                                {(!localStorage.getItem("swo") || parseInt(localStorage.getItem("swo")) < 3 || sessionStorage.getItem("customer")) && (
                                    <Form role="form">
                                      {/* NEW: Input for "Your Name" */}
                                      {!sessionStorage.getItem("customer") && <>
                                      <FormGroup className="mb-3">
                                        <InputGroup className="input-group-alternative">
                                          <InputGroupText>
                                            <i className="ni ni-circle-08" />
                                          </InputGroupText>
                                          <Input
                                              placeholder="Your Name"
                                              type="text"
                                              onChange={this.onChangeYourName}
                                          />
                                        </InputGroup>
                                      </FormGroup>
                                      </>}
                                      <p className="text-muted mb-3" style={{fontSize: '0.85rem'}}>
                                        Add one or more people to share this with
                                      </p>
                                      {this.state.recipients.map((recipient, index) => (
                                          <div key={index} className="d-flex align-items-start mb-2">
                                            <div style={{flex: 1, marginRight: '8px'}}>
                                              <Input
                                                  placeholder="Recipient's Name"
                                                  type="text"
                                                  value={recipient.name}
                                                  onChange={(e) => this.onChangeRecipientName(index, e.target.value)}
                                              />
                                            </div>
                                            <div style={{flex: 1, marginRight: this.state.recipients.length > 1 ? '8px' : '0'}}>
                                              <Input
                                                  placeholder="Email"
                                                  type="email"
                                                  value={recipient.email}
                                                  onChange={(e) => this.onChangeRecipientEmail(index, e.target.value)}
                                              />
                                              {recipient.emailError && (
                                                  <small className="text-danger">{recipient.emailError}</small>
                                              )}
                                            </div>
                                            {this.state.recipients.length > 1 && (
                                                <div style={{paddingTop: '6px'}}>
                                                  <Button
                                                      id={"remove-recipient-" + index}
                                                      color="link"
                                                      type="button"
                                                      className="p-0 text-muted"
                                                      onClick={() => this.removeRecipient(index)}
                                                  >
                                                    <RiCloseLine size={16} />
                                                  </Button>
                                                  <UncontrolledTooltip target={"remove-recipient-" + index} placement="top">
                                                    Remove this recipient
                                                  </UncontrolledTooltip>
                                                </div>
                                            )}
                                          </div>
                                      ))}
                                      <div className="mb-3">
                                        <Button
                                            id="add-recipient"
                                            color="link"
                                            type="button"
                                            className="p-0"
                                            onClick={this.addRecipient}
                                        >
                                          <RiAddLine size={14} />
                                          <span style={{marginLeft: '4px'}}>Add another person</span>
                                        </Button>
                                        <UncontrolledTooltip target="add-recipient" placement="top">
                                          Add another recipient
                                        </UncontrolledTooltip>
                                      </div>
                                      {this.state.shareError && (
                                        <p className="text-danger text-center mb-2" style={{fontSize: '0.85rem'}}>
                                          {this.state.shareError}
                                        </p>
                                      )}
                                      <div className="text-center">
                                        <Button
                                            className="my-4"
                                            color="primary"
                                            type="button"
                                            disabled={this.state.isSharing !== false}
                                            onClick={this.sharePlaylist}
                                        >
                                          <i className="fa fa-share-alt" aria-hidden="true"></i>
                                          <span className="btn-inner--text">
                                            {this.state.isSharing === "done" ? "✓ Shared" : this.state.isSharing ? "Sending…" : "Share song-flow"}
                                          </span>
                                        </Button>
                                      </div>
                                    </Form>
                                )}
                                {(localStorage.getItem("swo") && parseInt(localStorage.getItem("swo")) >= 3 && !sessionStorage.getItem("customer")) && (
                                    <div>
                                      <h3 style={{ fontSize: "1rem" }} className="text-primary text-uppercase mb-3">
                                        You've shared 3 worship plans
                                      </h3>
                                      <p className="description">
                                        We hope they've been useful! Create an account to keep sharing with your team, plus save and manage your plans without limits.
                                      </p>
                                      <div className="text-center">
                                        <Button
                                            className="my-4"
                                            color="primary"
                                            type="button"
                                            href="/register/choose-your-plan"
                                        >
                                          Create an account
                                        </Button>
                                      </div>
                                      <div className="text-center">
                                        <a href="/plans-and-pricing" style={{ fontSize: "0.85rem" }}>
                                          View plans &amp; pricing
                                        </a>
                                      </div>
                                    </div>
                                )}
                              </CardBody>
                            </Card>
                          </div>
                        </Modal>
                        <Modal
                            className="modal-dialog-centered"
                            size="sm"
                            isOpen={this.state.formModalSent}
                            toggle={() => this.toggleModal("formModalSent")}
                        >
                          <div className="modal-header">
                            <h2 style={{ fontSize: '1rem' }} className="modal-title" id="modal-title-default">
                              Share your curated song-flow ...
                            </h2>
                            <button
                                aria-label="Close"
                                className="close"
                                data-dismiss="modal"
                                type="button"
                                onClick={() => this.toggleModal("formModalSent")}
                            >
                              <span aria-hidden={true}>×</span>
                            </button>
                          </div>
                          <div className="modal-body p-0">
                            <Card className="bg-secondary shadow border-0">
                              <CardBody className="px-lg-5 py-lg-5">
                                <div className="text-center text-muted mb-4">
                                  Your song-flow was successfully shared
                                </div>
                                <Form role="form">
                                  <div className="text-center">
                                    <Button
                                        className="my-4"
                                        color="primary"
                                        type="button"
                                        onClick={() => this.toggleModal("formModalSent")}
                                    >
                                      <i className="fa fa-share-alt" aria-hidden="true"></i>
                                      <span className="btn-inner--text">Close</span>
                                    </Button>
                                  </div>
                                </Form>
                              </CardBody>
                            </Card>
                          </div>
                        </Modal>
                      </Col>
                    </Row>
                    {/*<div className="mt-5 py-5 border-top text-center">*/}
                    {/*  <div className="progress-wrapper">*/}
                    {/*    <div className="progress-info">*/}
                    {/*      <div className="progress-label">*/}
                    {/*        <span>WORSHIP SONG-FLOWS CREATION TASKS COMPLETED</span>*/}
                    {/*      </div>*/}
                    {/*      <div className="progress-percentage">*/}
                    {/*        <span>4/4</span>*/}
                    {/*      </div>*/}
                    {/*    </div>*/}
                    {/*    <Progress max="100" value="100" />*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                  </Col>
                </Row>
              </Container>
            </div>
          </section>
          <section className="section pt-0 pb-5">
            <Container>
              <CuratedSongFlowSignupBanner />
            </Container>
          </section>
        </>
    );
  }
}

export default withNavigateHook(CuratedSongsPicker);
